import React, {createContext, useContext, useEffect, useState} from 'react';
import {useSelector} from "react-redux";
import {Image} from "primereact/image";
import {usePost} from "../../models/post/use-post";
import {usePostCategory} from "../../models/post-category/use-post-category";
import Icon from "../../system/components/icon";
import {PanelMenu} from "primereact/panelmenu";
import {useNavigate, useParams} from "react-router-dom";
import {useTranslate} from "../../system/useTranslate";
import {Button} from "primereact/button";
import {Avatar as PrimeAvatar, Avatar} from "primereact/avatar";
import {DataView} from "primereact/dataview";
import {InputText} from "primereact/inputtext";
import {BreadCrumb} from "primereact/breadcrumb";
import {public_url} from "../../system/utils";
import {resolveUrl} from "../../system/app-config";
import Markdown from "markdown-to-jsx";


// Posts were merged into the help-center CMS (same id), so the old post page only forwards to the new design.
function Post() {
    const params = useParams();
    const navigate = useNavigate();
    useEffect(() => {
        navigate(`/help?view=article&id=${encodeURIComponent(params.id || "")}`, {replace: true});
    }, [navigate, params.id]);
    return null;
}

// eslint-disable-next-line no-unused-vars
function LegacyPost(props) {

    const [postData, setPostData] = useState();
    const post = usePost();
    const params = useParams();
    const translate = useTranslate();
    const ItemTemplate = (data)=>{
        return(
            <div className="w-full p-7 flex flex-column gap-4">
                <div className="flex align-items-center gap-3">
                    <span className="text-2xs font-bold bg-primary-100 text-primary pl-2 pr-2 p-1 border-round-xl">{data.post_category?.title}</span>
                    <span className="font-bold text-xl">{data.title}</span>
                    {data.timeToRead&&<div className="text-xs text-400 flex align-items-center gap-1"><Icon icon="mi mi:schedule" className="text-lg"/> {translate("time-to-read")} : {data.timeToRead}</div>}
                </div>
                <PostContent content={data.content} />
                <IsUsefull/>
                <RelatedPosts postData={data}/>
                <NeedHelp/>

            </div>
        )
    }


    useEffect(()=>{
        getPost();
    },[params.id])

    const getPost = async ()=>{
        let _data = await post.getPost(params.id);
        setPostData(_data);
    }


    return (
        <div className="h-full flex flex-column overflow-hidden">
            <Toolbar/>
            <div className="flex flex-1 h-full">
                <div className="w-20rem h-full border-left-1 border-300">
                    <Categories/>
                </div>
                <div className="h-full flex-1 overflow-auto">
                    {/*{params?.id}*/}
                    {/*<DataView value={[posts]} layout="list" itemTemplate={ItemTemplate}/>*/}
                    <ItemTemplate {...postData}/>

                </div>
            </div>

        </div>
    );
}

// ✅ کامپوننت PostContent فقط محتوای markdown را نمایش میدهد، بدون هرگونه مسیری به رندر HTML
export const PostContent = ({ content }) => {
    // هر محتوای غیرمتنی را به string تبدیل کنید و هرگونه HTML خام را حذف کنید
    const safeMarkdown = cleanMarkdownTableFragments(
        typeof content === "string" ? content : (content?.toString?.() ?? "")
    );

    return (
        <div className="w-full flex flex-column gap-3 rtl post-markdown text-sm line-height-3">
            <Markdown
                options={{
                    disableParsingRawHTML: true,
                    forceWrapper: true,
                    wrapper: "div",
                    overrides: {
                        a: { component: MarkdownSafeLink },
                        img: { component: MarkdownImage },
                        table: { component: MarkdownTable },
                    },
                }}
            >
                {safeMarkdown}
            </Markdown>
        </div>
    );
};

// ✅ جدولهای pipe که markdown-to-jsx نمیتواند parse کند (مثل سطرهای تکی `| | --- |` یا سطرهای خالی)
// به متن خام تبدیل میشوند. این تابع سطرهای شکسته/تنها که بخشی از یک جدول معتبر نیستند را حذف میکند
// تا در خروجی بهصورت متن زشت `| ... |` ظاهر نشوند. کلیه جداول معتبر دستنخورده میمانند.
const TABLE_SEPARATOR_RE = /^\s*\|? *:?-+:? *(?:\|[-| :]*)?\s*$/;
const PIPE_LINE_RE = /^\s*\|.*\|\s*$/;

function cellsIn(line) {
    const t = String(line).trim();
    if (!t.startsWith("|") || !t.endsWith("|")) return 0;
    return t.split("|").length - 2;
}

function cleanMarkdownTableFragments(markdown) {
    if (!markdown) return markdown;
    const lines = String(markdown).split("\n");
    const out = [];
    let i = 0;

    while (i < lines.length) {
        const line = lines[i];

        // شروع یک جدول معتبر: هدر `| ... |` که بلافاصله بعدش جداکننده `| --- |` با تعداد خانه یکسان دارد
        const isTableStart =
            PIPE_LINE_RE.test(line.trim()) &&
            i + 1 < lines.length &&
            TABLE_SEPARATOR_RE.test(lines[i + 1].trim()) &&
            cellsIn(line) > 0 &&
            cellsIn(line) === cellsIn(lines[i + 1]);

        if (isTableStart) {
            out.push(line);
            out.push(lines[i + 1]);
            i += 2;
            // سطرهای داده `| ... |` را ادامه میدهیم؛ اما اگر یک جدول جدید (هدر+جداکننده) یا جداکننده جداگانه آمد توقف کنید
            while (i < lines.length && PIPE_LINE_RE.test(lines[i].trim())) {
                const cur = lines[i];
                const next = lines[i + 1]?.trim() ?? "";
                const newTable = TABLE_SEPARATOR_RE.test(next) && cellsIn(cur) > 0 && cellsIn(cur) === cellsIn(next);
                if (TABLE_SEPARATOR_RE.test(cur.trim()) || newTable) break;
                out.push(cur);
                i += 1;
            }
            continue;
        }

        // سطر جداکننده/پایپ تکی که بخشی از جدول نیست → حذف کنید
        if (PIPE_LINE_RE.test(line.trim()) || TABLE_SEPARATOR_RE.test(line.trim())) {
            i += 1;
            continue;
        }

        out.push(line);
        i += 1;
    }

    return out.join("\n");
}

// ✅ برای اینکه PanelItem به expandedKeys دسترسی داشته باشه
const ExpandedKeysContext = createContext({});

// ✅ این تابع بیرون کامپوننت تعریف شده تا هر رندر تابع جدید ساخته نشه
const renderItemTemplate = (data, options) => {
    return <PanelItem data={data} options={options} />;
};

// ✅ کامپوننت واقعی آیتم (اینجا hook مجازه)
const PanelItem = ({ data, options }) => {
    const expandedKeys = useContext(ExpandedKeysContext);

    const params = useParams();
    const navigate = useNavigate();

    const activePostId = params?.id;
    const isPost = data?.data?.type === "post";
    const isActivePost = isPost && data?.value?.documentId === activePostId;

    const hasChildren = Array.isArray(data?.items) && data.items.length > 0;

    // ✅ منبع حقیقت expand: expandedKeys
    const isExpanded = !!expandedKeys?.[data.key];

    const handleClick = (e) => {
        if (isPost) {
            e.preventDefault();
            e.stopPropagation();
            navigate(`/help/${data.value.documentId}`);
            return;
        }

        // category => فقط expand/collapse
        if (typeof options?.onClick === "function") {
            options.onClick(e);
        }
    };

    return (
        <div className={`w-full cursor-pointer pr-3 pl-3 ${options.active&&"text-primary"}`} onClick={handleClick}>
            <div
                className={[
                    "flex justify-content-between align-items-center",
                    data?.data?.className || "",
                    isActivePost ? "bg-primary-50 border-primary border-right-3 text-primary" : "",
                ].join(" ")}>
                <span className="text-xs p-2">{data.label}</span>

                {hasChildren && (
                    <span>
                        <Icon icon={options.active ? "mi mi:keyboard_arrow_down" : "mi mi:keyboard_arrow_left"} />
                    </span>
                )}
            </div>
        </div>
    );
};

const Categories = () => {
    const [rawCategories, setRawCategories] = useState([]); // برای پیدا کردن مسیر
    const [model, setModel] = useState([]); // ✅ مدل پایدار
    const [expandedKeys, setExpandedKeys] = useState({}); // ✅ کنترل expand
    const postCategory = usePostCategory();
    const params = useParams();

    // 1) گرفتن دیتا و ساخت model فقط یک بار
    useEffect(() => {
        (async () => {
            const data = await postCategory.getPostCategories(["published"]);
            const raw = data || [];
            setRawCategories(raw);
            setModel(buildModel(raw));
        })();
    }, []);

    // 2) اگر params.id داریم: مسیر را پیدا کن و expand کن
    useEffect(() => {
        if (!params?.id) return;
        if (!rawCategories?.length) return;

        // کلیدهای categoryهایی که باید باز شوند
        const pathKeys = findCategoryPathKeysToPost(rawCategories, params.id);

        if (!pathKeys.length) return;

        const mustExpand = Object.fromEntries(pathKeys.map((k) => [k, true]));

        // اگر می‌خواهی فقط مسیر باز باشد: setExpandedKeys(mustExpand)
        // اگر می‌خواهی مسیر جدید به قبلی‌ها اضافه شود:
        setExpandedKeys((prev) => ({ ...prev, ...mustExpand }));
    }, [params?.id, rawCategories]);

    return (
        <ExpandedKeysContext.Provider value={expandedKeys}>
            <PanelMenu
                multiple
                dir="rtl"
                model={model}
                className="w-full pt-3"
                expandedKeys={expandedKeys}
                onExpandedKeysChange={(e) => setExpandedKeys(e.value)}
            />
        </ExpandedKeysContext.Provider>
    );
};

// ✅ این تابع بیرون کامپوننت: پایدار + بدون hook
function buildModel(rawCategories = []) {
    const mapNode = (
        item,
        className = "pr-2 font-bold border-bottom-1 border-200",
        type = "category"
    ) => {
        const key = item.documentId || String(item.id);

        const items =
            item.children?.length > 0
                ? item.children.map((child) => mapNode(child, "pr-3", "category"))
                : item.posts?.map((post) =>
                mapNode(post, "border-right-3 border-300 border-round-left mr-3", "post")
            ) || [];

        return {
            key,
            label: item.title,
            data: { className, type },
            value: item,
            items,
            template: renderItemTemplate, // ✅ رفرنس ثابت
        };
    };

    return rawCategories.map((c) => mapNode(c));
}

/**
 * ✅ مسیر categoryها تا رسیدن به postId را برمی‌گرداند (فقط category keys)
 * مثال خروجی: ["hxxxarra8v16tprejsnnnnuf", "uxg11rls024z4a25jdluo0tu"]
 */
function findCategoryPathKeysToPost(categories = [], postId) {
    const dfs = (nodes) => {
        for (const node of nodes) {
            const nodeKey = node.documentId || String(node.id);

            // پست داخل همین کتگوری؟
            const hasPost =
                Array.isArray(node.posts) &&
                node.posts.some((p) => (p.documentId || String(p.id)) === postId);

            if (hasPost) return [nodeKey];

            // داخل بچه‌ها؟
            if (Array.isArray(node.children) && node.children.length > 0) {
                const childPath = dfs(node.children);
                if (childPath) return [nodeKey, ...childPath];
            }
        }
        return null;
    };

    return dfs(categories) || [];
}


function MarkdownSafeLink({ children, href, ...props }) {
    const safeHref = /^(https?:|mailto:|\/|#)/i.test(String(href || "")) ? href : undefined;
    return <a {...props} href={safeHref} target="_blank" rel="noopener noreferrer">{children}</a>;
}

function MarkdownTable({ children, ...props }) {
    return (
        <div className="overflow-x-auto my-2">
            <table {...props}>{children}</table>
        </div>
    );
}

function MarkdownImage({ src, alt = "" }) {
    return (
        <div className="w-full flex justify-content-center my-3">
            <Image
                className="border-1 border-gray-300 border-round w-10"
                imageClassName="w-full border-round"
                preview
                src={resolveUrl(src)}
                alt={alt}
            />
        </div>
    );
}

function NeedHelp (){
    const translate = useTranslate();
    const navigate = useNavigate()




    return(
        <div className="w-full p-6 flex align-items-center justify-content-center">
            <div className="w-11 flex h-full justify-content-between surface-100 border-2 border-200 border-dashed border-round-xl p-3">
                <div className="flex flex-column">
                    <span className="font-bold text-xl">{translate("need-support-post")}</span>
                    <span className="text-400 text-xs">{translate("need-support-post-description")}</span>
                </div>
                <div className="flex align-items-center gap-2">
                    <Button className="text-xs p-button-plain p-button-help" label={translate("ask-question")}/>
                    <Button className="text-xs" label={translate("contact-support")}/>
                </div>
            </div>
        </div>

    )
}

function IsUsefull (){
    const translate = useTranslate();
    const navigate = useNavigate()




    return(
        <div className="w-full p-6 flex align-items-center justify-content-center">
            <div className="w-11 flex h-full justify-content-between surface-100 border-2 border-200 border-dashed border-round-xl p-3">
                <div className="flex flex-column">
                    <span className="font-bold text-xl">{translate("is-post-usefull")}</span>
                    <span className="text-400 text-xs">{translate("is-post-usefull-description")}</span>
                </div>
                <div className="flex align-items-center gap-2">
                    <Button className="text-xs p-1 border-round-lg pl-3 pr-3 p-button-secondary p-button-outlined flex align-items-center gap-2" icon={<Icon icon="mi mi:thumb_up"/>} label={translate("yes-usefull")}/>
                    <Button className="text-xs p-1 border-round-lg pl-3 pr-3 p-button-secondary p-button-outlined flex align-items-center gap-2" icon={<Icon icon="mi mi:thumb_down"/>} label={translate("not-usefull")}/>
                </div>
            </div>
        </div>

    )
}


function RelatedPosts ({postData}){
    const translate = useTranslate();
    const [posts, setPosts] = useState([]);
    const post = usePost();
    const navigate = useNavigate()

    // useEffect( ()=>{
    //     getPosts();
    // },[])
    //
    // const getPosts = async ()=>{
    //     let _data = await post.getPosts(["published"]);
    //     setPosts(_data);
    // }

    const ItemTemplate = (data)=>{
        return (
            <div className="w-6 p-2 cursor-pointer" onClick={()=>{navigate(`/help/${data.documentId}`)}}>
                <div className="border-1 gap-4 border-300 border-round-xl bg-white flex flex-column justify-content-between p-3 text-xs gap-2">
                    <div className="flex gap-1 align-items-center">
                        <Button label={translate(data.step)} disabled className={`p-button-text p-1 pr-2 pl-2 gap-2 flex text-xs ${data.step==="next-step"?"flex-row-reverse":"p-button-info"}`} icon={<Icon icon={`mi mi:${data.step==="next-step"?"keyboard_arrow_left":"keyboard_arrow_right"}`}/>}/>
                    </div>
                    <div className="flex gap-2 align-items-center">
                        <Avatar size="large" icon={<Icon icon={`mi mi:${data.icon??"lab_profile"}`}/>}/>
                        <span className="font-bold">{data.title}</span>
                    </div>
                </div>
            </div>
        )
    }

    return(
        <div className="w-full flex align-items-center justify-content-center">

            <div className="w-10 flex flex-column h-full justify-content-center">
                <span className="pr-2 font-bold">{translate("related-posts")}</span>
                <div className="flex gap-2">
                    {postData?.previous_post&&<ItemTemplate {...postData.previous_post} step="previous-step"/>}
                    {postData?.next_post&&<ItemTemplate {...postData.next_post} step="next-step"/>}
                </div>
                {/*<DataView className="flex flex-column" value={posts.slice(0,2)} layout="grid" itemTemplate={ItemTemplate}/>*/}
            </div>
        </div>

    )
}

function Toolbar() {
    const {config} = useSelector((state) => state.app);
    const translate = useTranslate();
    const navigate = useNavigate();

    return(
        <div className="w-full surface-100 border-bottom-1 border-300 p-2 flex align-items-center justify-content-between">
            <div className="flex align-items-center">
                <PrimeAvatar size="xlarge" className="h-auto cursor-pointer" image={public_url(config["sidebar-logo"])} onClick={() => navigate("/help")}/>
                <span className="w-1rem text-lg h-1rem border-right-2 border-300"/>
                <BreadCrumb className="bg-transparent border-none p-0" model={[{label:translate("home")}]}/>

            </div>
            <div className="flex align-items-center gap-2">
                <div className="flex align-items-center gap-1 surface-200 border-round pl-3 pr-3">
                    <Icon icon="pi pi-search" className="text-400 text-lg"/>
                    <InputText placeholder={translate("search")} className="p-3 w-16rem text-xs flex-1 shadow-none bg-transparent border-none"/>
                </div>
                <Button className="text-xs p-2 p-button-text p-button-secondary" label={translate("send-request")}/>
                <Button className="text-xs p-2" label={translate("sign-to-app")}/>

            </div>
        </div>
    )
}
function Footer() {
    const translate = useTranslate();
    return(
        <div className="p-3 pl-4 pr-4 flex align-items-center justify-content-between text-400">
            <div className="flex align-items-center gap-2">
                <Button className="p-button-secondary border-none shadow-none text-xs p-button-text p-2 pr-3 pl-3" label={translate("privacy")}/>
                <Button className="p-button-secondary border-none shadow-none text-xs p-button-text p-2 pr-3 pl-3" label={translate("rules")}/>
                <Button className="p-button-secondary border-none shadow-none text-xs p-button-text p-2 pr-3 pl-3" label={translate("system-status")}/>
            </div>
            <div>
                <span className="text-sm">© ۱۴۰۳ زوفینو. تمامی حقوق محفوظ است.</span>
            </div>
        </div>
    )
}

export default Post;
